import axios from "axios";
import { nanoid } from "nanoid";
import { captureMessage } from "@sentry/react";
const CryptoJS = require("crypto-js");

import { APP_ENV_MODE } from "constants/common";
import { LOGLEVEL, SENTRY_ERROR } from "constants/sentry";
import { trackAnalytics } from "services/analytics";
import Environment from "services/environment";
import Cipher from "services/cipher/index";
import showNotification from "utils/showNotification";
import { trackMOEServiceError } from "utils/analytics";
import { reportHttpErrorToSentry } from "utils/sentry";
import { enablePlatformLogin } from "utils/platformUtils";
import { fetchRbacToken } from "./services/profile";

const _axios = axios.create();
_axios.CancelToken = axios.CancelToken;
_axios.isCancel = axios.isCancel;

const cipher = new Cipher();
const appConfig = Environment.getEnv();
const NOTIFICATION_TIMEOUT = 15;
const channelName = "WEB";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  //FailedQueue stores all the pending promises of api returning 401 while another api invokes refresh token call to generate an updated access token
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isRefreshTokenCall = (request) => {
  const isPlatformLogin = enablePlatformLogin();
  const refreshTokenApiPath = isPlatformLogin ? "token" : "/refresh/token";
  const isRefresh =
    request.url.search(refreshTokenApiPath) !== -1 &&
    (request.data.includes("grant_type=refresh_token") || !isPlatformLogin);
  return isRefresh;
};

const refreshToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  const accessToken = localStorage.getItem("accessToken");
  const data = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: `${refreshToken}`,
    client_id: appConfig.CLIENT_ID,
  });
  let payload;
  if (enablePlatformLogin()) {
    payload = {
      method: "POST",
      url: "token",
      baseURL: appConfig.keycloakBaseUrl,
      data,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      config: {
        skipPayloadEncryption: true,
      },
    };
  } else {
    payload = {
      method: "GET",
      url: "user/v1/refresh/token",
      baseURL: appConfig.profileUnauthBaseUrl,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        refreshToken: `${refreshToken}`,
        accessToken: `${accessToken}`,
      },
    };
  }
  return RestApiCall.api(payload);
};

const decodeAccessToken = (accessToken) => {
  try {
    if (!accessToken) {
      return null;
    }
    let parts = accessToken.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT token format");
      return null;
    }
    const payloadBase64 = parts[1];
    let base64 = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) {
      base64 += "=".repeat(4 - pad);
    }
    const payload = JSON.parse(decodeURIComponent(escape(atob(base64))));
    return payload;
  } catch (error) {
    console.error("Error decoding access token:", error);
    return null;
  }
};

const fetchRbacTokenApi = async (accessToken, isReadOnlySuperAdminLogin = false) => {
  try {
    if (!accessToken) {
      return null;
    }
    const response = await fetchRbacToken({
      emailId: decodeAccessToken(accessToken)?.email,
      isReadOnlySuperAdmin: isReadOnlySuperAdminLogin,
    });
    if (response?.data?.code && response?.data?.message) {
      throw new Error(`Failed to fetch roles: ${response.data.message}`);
    } else {
      localStorage.setItem("rbacToken", response?.data?.rbacToken);
      localStorage.setItem("firstTimeUser", response?.data?.firstTimeUser);
      localStorage.setItem("profileType", response?.data?.profileType);
      localStorage.setItem("freshLogin", "true");
    }
  } catch (error) {
    console.error(error);
  }
};

const generateKey = () => `ATB_${APP_ENV_MODE}_${Date.now()}_${nanoid(30)}`;

const decryptDatafromKey = (data, key) => {
  let string = CryptoJS.DES.decrypt(data.replace('"', ""), CryptoJS.enc.Utf8.parse(key), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8);
  return string;
};

const encryptDataToSHAString = (data, key) => {
  let string = CryptoJS.DES.encrypt(data, CryptoJS.enc.Utf8.parse(key), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
  return string;
};

function fireServiceErrorAnalytics(error) {
  const { config, response } = error;
  const ignoreError = Boolean(config?.config?.ignoreError);
  trackAnalytics({
    prop25: "b2b|selfcare",
    eVar1: "b2b|selfcare|service error",
    eVar7: "b2b|selfcare|service error",
    eVar12: config?.url,
    eVar14: response?.data?.code,
    eVar15: response?.data?.message,
    eVar16: response?.status,
    eVar17: ignoreError,
  });
  const errorData = typeof response?.data === "string" ? { code: response?.data } : { ...response?.data };
  trackMOEServiceError({
    endpoint: config?.url,
    httpCode: response?.status,
    ignoreError,
    ...errorData,
  });
}

const showBusinessMessage = (res) => {
  showNotification({
    show: true,
    title: res.title,
    data: res.message,
    type: res.messageType,
    timer: NOTIFICATION_TIMEOUT,
  });
};

const isAuthApi = (baseURL) => ["/s/", "/ks/"].some((value) => baseURL.includes(value));

_axios.interceptors.request.use(
  (request) => {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    const rbacToken = localStorage.getItem("rbacToken");
    const appConfig = Environment.getEnv();

    // for ReadOnlySuperAdmin sending secondary customer emailId in headers
    const sIvUser = localStorage.getItem("s-iv-user");
    if (sIvUser && appConfig.ENABLE_READ_ONLY_SUPER_ADMIN_JOURNEY) {
      request.headers["s-iv-user"] = cipher.decrypt(sIvUser)["s-iv-user"];
    }

    if (isAuthApi(request.baseURL) && (!accessToken || !refreshToken)) {
      if (!request.config?.ignoreRedirection) logout();
      return Promise.reject(new axios.Cancel("Request cancelled by interceptor"));
    }
    // for dev
    // const accessToken = getCookie('accessToken');
    let key;
    if (!request.headers["adsHeader"]) {
      const uniqueId = `${JSON.stringify(request.data)}_${request.baseURL}_${request.url}`;
      key = generateKey();
    } else {
      key = request.headers["adsHeader"];
    }
    request.headers.common["googleCookie"] = "airtel.com";
    request.headers.common["adsHeader"] = key;

    if (!request.headers["Content-Type"]) {
      request.headers["Content-Type"] = "application/json";
    }

    if (appConfig.ivUser) {
      request.headers["iv-user"] = appConfig.ivUser;
    }

    console.log("Request for", request.url, JSON.stringify(request.data, null, 2));
    request.headers["requesterId"] = channelName;
    request.headers["pageURL"] = window.location.href;
    request.headers["app-id"] = "SELFCARE";

    if (enablePlatformLogin()) {
      request.headers["Authorization"] = rbacToken ? `Bearer ${rbacToken}` : "";
      request.headers["Authentication"] = `Bearer ${accessToken}`;
    } else {
      request.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    request.headers = { ...request.headers, ...request.config.headers };
    if (!request.headers["adsHeader"] && !request.config.skipPayloadEncryption) {
      if (request.method === "post" && request.data && !(request.config.file || request.config.files)) {
        request.data = encryptDataToSHAString(JSON.stringify(request.data), key);
      }
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  },
);

_axios.interceptors.response.use(
  (res) => {
    if (res.headers["googlecookie"] && res.data) {
      res.data = JSON.parse(decryptDatafromKey(res.data, res.headers["googlecookie"]));
    }
    console.log("Response for ", res.config.url, " ->", res.data);
    const isBusinessException = !!(res.data.code && res.data.message);
    const isBlobResponse = res.config.responseType === "blob";
    if (!(isBlobResponse || isBusinessException || res.headers["googlecookie"])) {
      captureMessage(SENTRY_ERROR.GOOGLE_COOKIE_HEADER, LOGLEVEL.INFO);
    }
    if (isBlobResponse && res.headers["content-type"] === "application/json") {
      // Business Error Received in place of Blob
      const reader = new FileReader();
      // This fires after the blob has been read/loaded.
      reader.addEventListener("loadend", (e) => {
        let textResponse = e.srcElement.result;
        let responseData = JSON.parse(textResponse);
        console.log("Extracted JSON from blob", responseData);
        if (responseData && responseData.code && responseData.message) {
          showBusinessMessage(responseData);
        }

        return res;
      });
      // Start reading the blob as text.
      reader.readAsText(res.data);
    } else if (isBusinessException) {
      console.log("Show Business Notifications");
      showBusinessMessage(res.data);
    }
    return res;
  },
  (error) => {
    reportHttpErrorToSentry(error); // for capturing api fingerprint in sentry

    if (isRequestCancelled(error)) return Promise.reject(error);
    if (!error.response) return Promise.reject(new Error("Unknown API error occured."));
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshTokenCall(originalRequest)) {
        logout();
      }
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject }); //Pushing the pending promise to the queue
        })
          .then((token) => {
            //Called when the promise is resolved and api is called with updated access token
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return _axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      originalRequest._retry = true; //So that same request would not be called
      isRefreshing = true; //To handle concurrent api calling scenerio
      return new Promise(function (resolve, reject) {
        refreshToken()
          .then(async ({ data }) => {
            const accessToken = enablePlatformLogin() ? data.access_token : data.accessToken;
            localStorage.setItem("accessToken", accessToken);
            // for dev
            // setCookie('accessToken', accessToken);
            if(enablePlatformLogin()) {
              await fetchRbacTokenApi(accessToken);
              const rbacToken = localStorage.getItem("rbacToken");
              _axios.defaults.headers.common["Authentication"] = `Bearer ${accessToken}`;
              originalRequest.headers["Authentication"] = `Bearer ${accessToken}`;
              _axios.defaults.headers.common["Authorization"] = rbacToken ? `Bearer ${rbacToken}` : "";
              originalRequest.headers["Authorization"] = rbacToken ? `Bearer ${rbacToken}` : "";
            } else {
              _axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`; //Change default header for _axios
              originalRequest.headers["Authorization"] = `Bearer ${accessToken}`; //Change header for the current request
            }
            processQueue(null, accessToken); //Call process queue to resolve any pending requests
            resolve(_axios(originalRequest)); //Resolving or calling the current api again
          })
          .catch((err) => {
            processQueue(err, null);
            reject(err);
          })
          .then(() => {
            isRefreshing = false;
          });
      });
    } else {
      fireServiceErrorAnalytics(error);
      if (error.config && error.config.config && !error.config.config.ignoreError) {
        const errorCode = error.response?.data?.code?.toUpperCase();
        const errorNotification = {
          title: "Service Error",
          message: "We faced some intermittent issue. Please try again",
        };
        if (errorCode?.includes("FALLBACK") || errorCode?.includes("TIMEOUT")) {
          errorNotification.title = "Timed Out";
          errorNotification.message = "Your request was timed out. Please try again";
        }
        showBusinessMessage({
          ...errorNotification,
          messageType: "ERROR",
        });
      }
    }
    return Promise.reject(error);
  },
);

function logout() {
  if (enablePlatformLogin()) {
    window.location.href = appConfig.selfcareLoginPageBaseUrl + "logout";
    return;
  }
  sessionStorage.clear();
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("trackOrdertoken"); // for track order journey (Data)
  localStorage.removeItem("trackOrdercpqOrderId"); // for track order journey (Data)
  localStorage.removeItem("s-iv-user"); // for ReadOnly Super Admin journey
  window.location.href = appConfig.selfcareLoginPageBaseUrl;
}
const generateCancelToken = () => _axios.CancelToken?.source();
const isRequestCancelled = (error) => _axios.isCancel(error);

class RestApiCall {
  static api(payload) {
    const apiPayload = {
      url: payload.url || "",
      method: payload.method || "get",
      baseURL: payload.baseURL || "",
      headers: payload.headers || {},
      params: payload.params || {},
      data: payload.data || {},
      config: payload.config || {},
      responseType: payload.responseType,
      cancelToken: payload.cancelToken,
    };

    return new Promise(function (resolve, reject) {
      _axios(apiPayload)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

export { generateCancelToken, isRequestCancelled };
export default RestApiCall;
